import type {
  DictsysFrenchNounInflectionScheme,
  DictsysInflectionSchemeCell,
  DictsysInflectionSchemeContent,
  DictsysInflectionSchemeFrenchNounArticleType,
  DictsysInflectionSchemeFrenchNounGender,
  DictsysInflectionSchemeTable
} from "@/types";

type GenderType = keyof DictsysInflectionSchemeFrenchNounGender;
type NounType = keyof DictsysInflectionSchemeFrenchNounArticleType;

function getBodyCell(content: DictsysInflectionSchemeContent[] | undefined): DictsysInflectionSchemeCell {
  const cell: DictsysInflectionSchemeCell = { elements: [], type: "body" };

  if (content === undefined || content.length === 0) {
    return cell;
  }

  const texts = [content[0].index, ...content.slice(1).map((i) => i.inflexion)];
  cell.elements = texts.map((text) => ({ texts: [text] }));
  return cell;
}

function getNouns(
  text: string,
  article: DictsysInflectionSchemeFrenchNounArticleType | undefined,
  noun: NounType
): DictsysInflectionSchemeCell[] | null {
  if (!article) {
    return null;
  }

  return [
    { elements: [{ texts: [text] }], type: "sub-header" },
    getBodyCell(article[noun]["singular"]),
    getBodyCell(article[noun]["plural"])
  ];
}

function getTable(
  noun: DictsysInflectionSchemeFrenchNounGender,
  gender: GenderType
): DictsysInflectionSchemeTable | null {
  const article: DictsysInflectionSchemeFrenchNounArticleType = noun[gender];

  const definite = getNouns("Bestemt", article, "definite");
  const indefinite = getNouns("Ubestemt", article, "indefinite");

  if (definite === null || indefinite === null) {
    return null;
  }

  const header = gender === "masculine" ? "Maskulinum (hankøn)" : "Femininum (hunkøn)";
  return {
    header: header,
    cells: [
      [
        { type: "sub-header", elements: [{}] },
        { elements: [{ texts: ["Singularis"], comment: "(ental)" }], type: "sub-header" },
        { elements: [{ texts: ["Pluralis"], comment: "(flertal)" }], type: "sub-header" }
      ],
      definite,
      indefinite
    ]
  };
}

export function getTables(inflectionScheme: DictsysFrenchNounInflectionScheme): DictsysInflectionSchemeTable[] {
  if (!inflectionScheme.noun) {
    return [];
  }

  const tables: DictsysInflectionSchemeTable[] = [];

  const masculine = getTable(inflectionScheme.noun, "masculine");
  if (masculine) {
    tables.push(masculine);
  }

  const feminine = getTable(inflectionScheme.noun, "feminine");
  if (feminine) {
    tables.push(feminine);
  }

  return tables;
}
